/**
 * ============================================================================
 * ADMIN ANALYTICS PAGE
 * ============================================================================
 * Admin analytics dashboard: hero, tabs, overview/transactions/users/revenue/disputes.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../providers';
import { useI18n } from '../../providers';
import AdminLayout from '../../components/admin/AdminLayout';
import Card from '../../components/common/Card';
import Tabs from '../../components/common/Tabs';
import {
  AdminAnalyticsOverview,
  AdminTransactionAnalytics,
  AdminUserAnalytics,
  AdminRevenueAnalytics,
  AdminDisputeAnalytics,
  ExportAnalytics,
} from '../../components/analytics';
import '../../styles/landing.css';
import '../../styles/animations.css';
import '../../styles/global.css';

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated } = useAuthContext();
  const { language } = useI18n();
  const [activeTab, setActiveTab] = useState('overview');

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    if (authUser?.role !== 'admin' && authUser?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
  }, [isAuthenticated, authUser, navigate]);

  const tabs = [
    { id: 'overview', label: language === 'fr' ? "Vue d'ensemble" : 'Overview', icon: '📊' },
    { id: 'transactions', label: language === 'fr' ? 'Transactions' : 'Transactions', icon: '📦' },
    { id: 'users', label: language === 'fr' ? 'Utilisateurs' : 'Users', icon: '👥' },
    { id: 'revenue', label: language === 'fr' ? 'Revenus' : 'Revenue', icon: '💰' },
    { id: 'disputes', label: language === 'fr' ? 'Litiges' : 'Disputes', icon: '⚠️' },
  ];

  const getCurrentFilters = () => ({ timeRange: '30d' });

  return (
    <AdminLayout>
      <div className="transactions-wt dashboard-wt">
        {/* Hero */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />

          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              📊 {language === 'fr' ? 'Analytique Admin' : 'Admin Analytics'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Analysez les performances du système et suivez les métriques en temps réel'
                : 'Analyze system performance and track metrics in real-time'}
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-6">
              <button
                type="button"
                className="dashboard-wt-btn-primary dashboard-wt-btn-large"
                onClick={() => navigate('/admin/dashboard')}
              >
                <span className="dashboard-wt-btn-icon">←</span>
                {language === 'fr' ? 'Retour au tableau de bord' : 'Back to Dashboard'}
              </button>
              {activeTab !== 'overview' && (
                <ExportAnalytics
                  analyticsType={activeTab}
                  filters={getCurrentFilters()}
                />
              )}
            </div>
          </div>
        </section>

        {/* Tabs */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className="dashboard-wt-card">
              <div className="dashboard-wt-card-header">
                <h2 className="dashboard-wt-card-title text-base font-semibold">
                  {language === 'fr' ? 'Sections' : 'Sections'}
                </h2>
              </div>
              <div className="dashboard-wt-card-content pt-2 pb-4">
                <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} variant="pills-green" />
              </div>
            </div>
          </div>
        </section>

        {/* Tab Content */}
        <section className="dashboard-wt-section dashboard-wt-section-alt">
          <div className="dashboard-wt-section-inner">
            {activeTab === 'overview' && <AdminAnalyticsOverview />}
            {activeTab === 'transactions' && <AdminTransactionAnalytics />}
            {activeTab === 'users' && <AdminUserAnalytics />}
            {activeTab === 'revenue' && <AdminRevenueAnalytics />}
            {activeTab === 'disputes' && <AdminDisputeAnalytics />}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
};

export default AdminAnalytics;
